import { Observable } from "rxjs";
import { map } from "rxjs/operators";

import type { 
  StorageEntity, 
  StorageEntityAPI, 
  PaginatedResponse,
  FilterType,
  FilterSelect,
  FilterOrderBy,
  UpdateStorageLimitBatchResponse
} from "../types/types";
import { APIHosts, ApiReduxHelpers } from "@/services/reduxHelpers";
import HttpClient from "@/services/HttpClient";

/**
 * Storage Service
 * Handles Company Storage Management operations
 * Follows DriveService pattern with RxJS Observables
 */

/**
 * Transform API entity to frontend entity format
 */
function transformEntity(apiEntity: StorageEntityAPI): StorageEntity {
  return {
    id: apiEntity.id,
    name: apiEntity.driveName,
    type: apiEntity.type === 'company' ? 'company' : 
          apiEntity.type === 'team' ? 'department' : 'user',
    storageUsed: apiEntity.storageSize,
    limitStorage: apiEntity.capacitySize,
    totalDocuments: apiEntity.totalDocuments,
    totalSubFolders: apiEntity.totalSubFolders,
    totalUsers: apiEntity.totalUsers,
    usagePercentage: apiEntity.usagePercentage,
    isOverQuota: apiEntity.isOverQuota,
    lastUpdated: apiEntity.lastUpdated,
    createdDate: apiEntity.createdDate
  };
}

class StorageServiceController extends ApiReduxHelpers {
  constructor() {
    super();
  }

  ApiHost = APIHosts.Upload;

  /**
   * Fetch storage entities with pagination and filters
   * GET /Drive/company/{companyId}
   * Query params: filter, select, orderBy, page, pageSize
   */
  public fetchStorageEntities = (
    companyId: string,
    params: {
      filter?: FilterType;
      select?: FilterSelect;
      orderBy?: FilterOrderBy;
      page?: number;
      pageSize?: number;
      search?: string;
    }
  ): Observable<PaginatedResponse<StorageEntity>> => {
    const url = `${this.getHost(this.ApiHost)}/Drive/company/${companyId}`;
    
    // Build query parameters
    const queryParams = new URLSearchParams();
    // Only add filter param if not empty (empty means 'all')
    if (params.filter) queryParams.append('filter', params.filter);
    if (params.select) queryParams.append('select', params.select);
    if (params.orderBy) queryParams.append('orderBy', params.orderBy);
    if (params.page !== undefined) queryParams.append('page', String(params.page));
    if (params.pageSize !== undefined) queryParams.append('pageSize', String(params.pageSize));
    if (params.search) queryParams.append('search', params.search);
    
    const fullUrl = `${url}?${queryParams.toString()}`;
    
    return HttpClient.get<{
      results: StorageEntityAPI[];
      page: number;
      pageCount: number;
      pageSize: number;
      queryCount: number;
      firstRowIndex: number;
      lastRowIndex: number;
    }>(fullUrl).pipe(
      map((response) => ({
        results: response.results.map(transformEntity),
        page: response.page,
        pageCount: response.pageCount,
        pageSize: response.pageSize,
        queryCount: response.queryCount,
        firstRowIndex: response.firstRowIndex,
        lastRowIndex: response.lastRowIndex
      }))
    );
  };

  /**
   * Update storage capacity limit for multiple drives (batch)
   * PUT /Drive/quota/batch
   * Body: { driveIds: string[], capacitySizeGB: number }
   */
    public updateStorageLimitBatch = (
        driveIds: string[],
        capacitySizeGB: number
    ): Observable<StorageEntity[]> => {
        const url = `${this.getHost(this.ApiHost)}/Drive/quota/batch`;

        return HttpClient.put<UpdateStorageLimitBatchResponse>(
            url,
            { driveIds, capacitySizeGB }
        ).pipe(
            map((response) =>
                response.successResults.map((result) =>
                    transformEntity(result.drive)
                )
            )
        );
    };

  /**
   * Check if user root exists (for compatibility with DriveService pattern)
   * GET /Drive/company/{companyId}
   */
  public checkUserRoot = (companyId: string): Observable<string> => {
    return this.fetchStorageEntities(companyId, { 
      filter: 'company', 
      page: 1, 
      pageSize: 1 
    }).pipe(
      map((response) => {
        if (response.results.length > 0) {
          return response.results[0].id;
        }
        throw new Error('Company root not found');
      })
    );
  };
}

// Export singleton instance
export const StorageService = new StorageServiceController();
